import { useEffect, useState, type ReactNode } from 'react'
import { AUTH_SESSION_REFRESH_EVENT, AuthSessionError, auth, type AuthSession } from '../lib/auth'
import { AuthContext } from './auth-context'

interface AuthProblem {
  title: string
  message: string
}

function authProblemFromUrl(): AuthProblem | null {
  const reason = new URLSearchParams(window.location.search).get('auth_error')
  if (!reason) return null
  if (reason === 'access_denied' || reason === 'cancelled' || reason === 'login_cancelled') {
    return {
      title: 'Sign-in cancelled',
      message: 'The sign-in flow was cancelled before a session was created.',
    }
  }
  return {
    title: 'Sign-in failed',
    message: `The sign-in flow ended with: ${reason}.`,
  }
}

function authProblemFromError(err: unknown): AuthProblem {
  if (err instanceof AuthSessionError && err.status === 403) {
    return {
      title: 'Access required',
      message: 'You are signed in, but this user does not have access to the selected organization.',
    }
  }
  if (err instanceof AuthSessionError) {
    return {
      title: 'Session check failed',
      message: err.message,
    }
  }
  return {
    title: 'Session check failed',
    message: 'The app could not verify your session.',
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [problem, setProblem] = useState<AuthProblem | null>(() => authProblemFromUrl())
  const [loading, setLoading] = useState(() => authProblemFromUrl() === null)

  useEffect(() => {
    if (problem) return undefined

    let cancelled = false
    const loadSession = (redirectUnauthenticated: boolean) => {
      auth.getSession()
        .then((s) => {
          if (cancelled) return
          setSession(s)
          setLoading(false)
          if (redirectUnauthenticated && !s.authenticated) {
            auth.signIn()
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return
          setProblem(authProblemFromError(err))
          setLoading(false)
        })
    }
    loadSession(true)
    const onRefresh = () => {
      loadSession(false)
    }
    window.addEventListener(AUTH_SESSION_REFRESH_EVENT, onRefresh)
    return () => {
      cancelled = true
      window.removeEventListener(AUTH_SESSION_REFRESH_EVENT, onRefresh)
    }
  }, [problem])
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-app-bg text-text-muted">
        Loading…
      </div>
    )
  }

  if (!session?.authenticated) {
    if (problem) {
      return (
        <div className="flex h-screen items-center justify-center bg-app-bg px-4 text-text-default">
          <div className="w-full max-w-sm rounded-lg border border-border-default bg-white p-6 shadow-sm">
            <h1 className="text-base font-semibold">{problem.title}</h1>
            <p className="mt-2 text-sm text-text-muted">{problem.message}</p>
            <button
              type="button"
              onClick={() => {
                auth.clearCache()
                auth.signIn()
              }}
              className="mt-5 rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Sign in
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="flex h-screen items-center justify-center bg-app-bg text-text-muted">
        Redirecting to login…
      </div>
    )
  }

  return <AuthContext.Provider value={session}>{children}</AuthContext.Provider>
}
